import { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { useMsal, useAccount } from '@azure/msal-react';
import { useUser } from '../lib/auth';
import api from '../lib/api';
import { accountIsFounder } from '../lib/founders';

type Role = 'ceo' | 'manager' | 'hr' | 'recruiter' | 'admin' | 'coordinator' | 'viewer' | 'pending' | null;

// Highest-privilege wins when a user holds multiple roles — the badge + any
// role-based nav gating should reflect their top role.
const ROLE_PRECEDENCE: Role[] = ['ceo', 'admin', 'manager', 'hr', 'recruiter', 'coordinator', 'viewer', 'pending'];
function topRole(roleKeys: string[]): Role {
  // platform_owner / platform_admin are founder-equivalent: the catalog in
  // every tenant. Role-name checks (badge, legacy nav, dashboards) treat them
  // as CEO, as they do a founder. The backend stays the authority.
  if (roleKeys.includes('platform_owner') || roleKeys.includes('platform_admin')) return 'ceo';
  for (const r of ROLE_PRECEDENCE) {
    if (r && roleKeys.includes(r)) return r;
  }
  return roleKeys[0] as Role ?? null;
}

interface RBACContextValue {
  role: Role;
  isLoading: boolean;
  can: (permission: string) => boolean;
  hasRole: (roles: Role[]) => boolean;
}

// Permission map matching backend PERMISSIONS
const PERMISSIONS: Record<string, string[]> = {
  system_settings:      ['ceo', 'admin'],
  user_management:      ['ceo', 'manager', 'admin'],
  candidates_view:      ['ceo', 'manager', 'hr', 'recruiter', 'admin', 'coordinator'],
  candidates_create:    ['ceo', 'manager', 'hr', 'recruiter', 'admin', 'coordinator'],
  candidates_edit:      ['ceo', 'manager', 'hr', 'admin', 'coordinator'],
  candidates_delete:    ['ceo', 'manager', 'admin'],
  candidate_stage_move: ['ceo', 'manager', 'hr', 'admin'],
  resume_upload:        ['ceo', 'manager', 'hr', 'recruiter', 'admin', 'coordinator'],
  credentialing_view:   ['ceo', 'manager', 'hr', 'recruiter', 'admin', 'coordinator'],
  credentialing_manage: ['ceo', 'manager', 'hr', 'admin'],
  onboarding_view:      ['ceo', 'manager', 'hr', 'admin', 'coordinator'],
  onboarding_manage:    ['ceo', 'manager', 'hr', 'admin'],
  staff_view:           ['ceo', 'manager', 'hr', 'recruiter', 'admin', 'coordinator'],
  staff_manage:         ['ceo', 'manager', 'hr', 'admin'],
  placements_view:      ['ceo', 'manager', 'hr', 'admin', 'coordinator'],
  placements_manage:    ['ceo', 'manager', 'admin'],
  financials_view:      ['ceo', 'admin'],
  rates_view:           ['ceo', 'manager', 'admin'],
  reminders_manage:     ['ceo', 'manager', 'hr', 'admin'],
  all_reports:          ['ceo', 'manager', 'admin'],
  team_reports:         ['ceo', 'manager', 'hr', 'admin'],
};

const RBACContext = createContext<RBACContextValue>({
  role: null,
  isLoading: true,
  can: () => false,
  hasRole: () => false,
});

export function RBACProvider({ children }: { children: ReactNode }) {
  const { user, isLoaded } = useUser();
  const { accounts } = useMsal();
  const account = useAccount(accounts[0] ?? null);
  const isFounder = accountIsFounder(account);
  const [fetchedRole, setFetchedRole] = useState<Role>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!isLoaded) return;
    if (!user) { setIsLoading(false); return; }

    // Resolve the REAL role from the backend (rbac_user_roles), not Clerk
    // metadata — which is always empty under Azure AD and used to make every
    // user show as "coordinator". The badge + role-based nav now reflect the
    // user's actual assigned role(s).
    let cancelled = false;
    api.get<{ roles: string[] }>('/rbac/my-permissions')
      .then((res) => {
        if (cancelled) return;
        setFetchedRole(topRole(res.data.roles ?? []));
      })
      .catch(() => { if (!cancelled) setFetchedRole(null); })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, [user, isLoaded]);

  // Founder failsafe — platform owners always show as CEO, even if the
  // backend role fetch fails or is rate-limited. Backend enforces the same
  // allowlist on real API calls.
  const role: Role = isFounder ? 'ceo' : fetchedRole;

  // Memoized so the context value is stable across renders — otherwise every
  // useRBAC/useCan/useRole consumer (notably the always-mounted Sidebar) would
  // re-render on any provider render (e.g. MSAL token events).
  const can = useCallback((permission: string): boolean => {
    if (isFounder) return true;
    if (!role) return false;
    const allowed = PERMISSIONS[permission];
    if (!allowed) return true; // Unknown permission — allow
    return allowed.includes(role);
  }, [isFounder, role]);

  const hasRole = useCallback((roles: Role[]): boolean => {
    if (!role) return false;
    return roles.includes(role);
  }, [role]);

  const value = useMemo(() => ({ role, isLoading, can, hasRole }), [role, isLoading, can, hasRole]);

  return (
    <RBACContext.Provider value={value}>
      {children}
    </RBACContext.Provider>
  );
}

export function useRBAC() {
  return useContext(RBACContext);
}

export function useRole() {
  return useContext(RBACContext).role;
}

export function useCan(permission: string) {
  const { can } = useContext(RBACContext);
  return can(permission);
}
